// @ts-strict-ignore
import React, { useCallback, useMemo } from "react";

import { useI18nLocale } from "@braze/i18n-core";

import countriesData from "../../utils/CountryInfo.json";
import { sortCountryInfoByLocale } from "../../utils/sortCountriesByLocale";
import { CountryCodeSelectContainer } from "./CountryCodeSelect.style";
import { CountryCodeSelectOption } from "./CountryCodeSelectOption";
import type { CountryCodeSelectProps, CountryCodeType } from "./types";

export const CountryCodeSelect = React.forwardRef<
  HTMLSelectElement,
  React.PropsWithChildren<CountryCodeSelectProps>
>(({ onChange, value, invalid, onBlur, isDisabled }, ref) => {
  const currentLocale = useI18nLocale();

  const options: CountryCodeType[] = useMemo(() => {
    const sortedCountries = sortCountryInfoByLocale(countriesData, currentLocale);
    return sortedCountries.map((country) => ({ ...country, value: country.code }));
  }, [currentLocale]);

  const handleChange = useCallback((option: CountryCodeType) => onChange(option.code), [onChange]);
  const selectedValue = useMemo(() => options.find((country) => country.code === value), [value, options]);

  const memoizedFilterOption = useCallback((option, inputValue) => {
    const country = option.data as CountryCodeType;
    const searchValue = inputValue.trim().toLowerCase().replace("+", "");
    return (
      country.country.toLowerCase().includes(searchValue) || country.phoneCode.toString().startsWith(searchValue)
    );
  }, []);

  return (
    <CountryCodeSelectContainer
      options={options}
      onChange={handleChange}
      value={selectedValue}
      menuPortalTarget={document.body}
      filterOption={memoizedFilterOption}
      fluid
      isClearable={false}
      maxMenuHeight={200}
      formatOptionLabel={CountryCodeSelectOption}
      applyOptionFormattingToValue
      ref={ref}
      invalid={invalid}
      onBlur={onBlur}
      isDisabled={isDisabled}
    />
  );
});
